import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ReportHeader from './components/ReportHeader';
import ExecutiveSummary from './components/ExecutiveSummary';
import RiskHeatmap from './components/RiskHeatmap';
import DetailedAnalysis from './components/DetailedAnalysis';
import MitigationStrategies from './components/MitigationStrategies';
import ReportActions from './components/ReportActions';

const ExecutiveReport = () => {
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const companyData = {
    name: "PT Rumah Susu Yova",
    address: "Jl. Raya Susu No. 123, Bandung, Jawa Barat",
    industry: "Pengolahan Produk Susu",
    established: "2018"
  };

  const reportDate = "01 Februari 2026";
  const reportId = "DD-ISUKA-2026-001";

  const summaryData = {
    overallScore: 72,
    supplyScore: 68,
    operationalScore: 75,
    financialScore: 70,
    keyFindings: [
      {
        type: "positive",
        text: "Kapasitas produksi saat ini mencapai 85% utilisasi dengan potensi scale-up hingga 150% tanpa investasi peralatan besar"
      },
      {
        type: "warning",
        text: "Ketergantungan pada 3 pemasok utama (78% total pasokan) menciptakan risiko supply chain yang signifikan"
      },
      {
        type: "negative",
        text: "Fluktuasi pasokan susu mencapai 35% selama musim kemarau dengan tingkat penolakan kualitas 12%"
      },
      {
        type: "warning",
        text: "Margin operasional 18% tertekan oleh biaya logistik tinggi dan ketergantungan pada pembeli institusional (65% revenue)"
      },
      {
        type: "positive",
        text: "Struktur tata kelola solid dengan sistem kontrol kualitas terintegrasi dan dokumentasi operasional lengkap"
      }
    ],
    mainRecommendation: "Berdasarkan analisis komprehensif, kami merekomendasikan strategi Scale-Up Terkontrol dengan fokus pada diversifikasi pemasok, optimalisasi operasional, dan pengurangan ketergantungan pembeli institusional. Implementasi bertahap selama 6-9 bulan dengan investasi prioritas pada sistem buffer supply dan peningkatan kapasitas penyimpanan dingin."
  };

  const riskData = [
    {
      category: "Risiko Rantai Pasokan",
      level: "high",
      score: 78,
      description: "Ketergantungan tinggi pada pemasok terbatas dengan fluktuasi pasokan musiman signifikan",
      probability: 75,
      impact: 85
    },
    {
      category: "Risiko Operasional",
      level: "medium",
      score: 62,
      description: "Bottleneck pada proses pasteurisasi dan keterbatasan kapasitas penyimpanan dingin",
      probability: 60,
      impact: 65
    },
    {
      category: "Risiko Keuangan",
      level: "medium",
      score: 58,
      description: "Margin tertekan dengan cashflow sensitif terhadap fluktuasi harga susu dan biaya operasional",
      probability: 55,
      impact: 70
    },
    {
      category: "Risiko Pasar",
      level: "high",
      score: 72,
      description: "Ketergantungan berlebihan pada pembeli institusional dengan diversifikasi produk terbatas",
      probability: 70,
      impact: 80
    },
    {
      category: "Risiko Peralatan",
      level: "medium",
      score: 54,
      description: "Peralatan aging dengan maintenance reaktif dan tidak ada rencana penggantian sistematis",
      probability: 50,
      impact: 60
    },
    {
      category: "Risiko Tata Kelola",
      level: "low",
      score: 35,
      description: "Struktur organisasi solid dengan sistem kontrol memadai namun perlu penguatan succession planning",
      probability: 30,
      impact: 45
    }
  ];

  const analysisData = [
    {
      id: "supply",
      type: "supply",
      title: "Analisis Rantai Pasokan",
      overview: "Evaluasi komprehensif terhadap struktur pemasok, stabilitas pasokan, dan sistem manajemen kualitas susu. Analisis mencakup pola fluktuasi musiman, tingkat penolakan, dan ketergantungan pemasok.",
      metrics: [
        { icon: "Users", label: "Total Pemasok", value: "47", unit: "peternak" },
        { icon: "TrendingDown", label: "Fluktuasi Pasokan", value: "35%", unit: "musiman" },
        { icon: "XCircle", label: "Tingkat Penolakan", value: "12%", unit: "rata-rata" }
      ],
      findings: [
        {
          type: "negative",
          text: "3 pemasok utama menyumbang 78% total pasokan, menciptakan konsentrasi risiko tinggi"
        },
        {
          type: "warning",
          text: "Fluktuasi pasokan mencapai 35% selama musim kemarau (Juni-September) tanpa sistem buffer memadai"
        },
        {
          type: "warning",
          text: "Tingkat penolakan kualitas 12% dengan pola berulang pada pemasok tertentu"
        },
        {
          type: "positive",
          text: "Sistem pembayaran tepat waktu mempertahankan loyalitas pemasok dengan churn rate rendah (8%)"
        }
      ],
      recommendations: [
        "Diversifikasi pemasok dengan target mengurangi konsentrasi 3 pemasok utama dari 78% menjadi maksimal 50% dalam 6 bulan",
        "Implementasi sistem buffer supply dengan kapasitas penyimpanan dingin tambahan 5.000 liter untuk mengatasi fluktuasi musiman",
        "Program peningkatan kualitas pemasok dengan training dan monitoring intensif untuk mengurangi rejection rate menjadi di bawah 5%",
        "Pengembangan kontrak jangka panjang dengan insentif volume untuk mengamankan pasokan stabil"
      ]
    },
    {
      id: "operational",
      type: "operational",
      title: "Analisis Operasional",
      overview: "Pemetaan alur produksi end-to-end dengan identifikasi bottleneck, analisis kapasitas, dan evaluasi efisiensi operasional. Fokus pada readiness untuk scale-up dan optimalisasi proses.",
      metrics: [
        { icon: "Activity", label: "Utilisasi Kapasitas", value: "85%", unit: "saat ini" },
        { icon: "Clock", label: "Waktu Proses", value: "4.5", unit: "jam/batch" },
        { icon: "AlertTriangle", label: "Bottleneck Kritis", value: "2", unit: "titik" }
      ],
      findings: [
        {
          type: "positive",
          text: "Utilisasi kapasitas 85% menunjukkan ruang untuk scale-up 15-20% tanpa investasi besar"
        },
        {
          type: "negative",
          text: "Bottleneck pada proses pasteurisasi (kapasitas 3.000 liter/batch) membatasi throughput maksimal"
        },
        {
          type: "warning",
          text: "Kapasitas penyimpanan dingin hanya 8.000 liter, tidak cukup untuk buffer supply 3 hari"
        },
        {
          type: "positive",
          text: "Proses packaging efisien dengan automation 70% dan waste rate rendah (3%)"
        }
      ],
      recommendations: [
        "Upgrade pasteurizer dari 3.000 liter menjadi 5.000 liter per batch untuk menghilangkan bottleneck utama",
        "Tambah kapasitas cold storage dari 8.000 liter menjadi 15.000 liter untuk buffer supply 5 hari",
        "Implementasi sistem monitoring real-time untuk tracking efisiensi dan early warning system",
        "Optimalisasi shift scheduling untuk meningkatkan utilisasi peralatan menjadi 95%"
      ]
    },
    {
      id: "equipment",
      type: "equipment",
      title: "Analisis Peralatan",
      overview: "Evaluasi kondisi peralatan existing, analisis maintenance history, dan identifikasi kebutuhan investasi untuk mendukung scale-up. Termasuk assessment terhadap equipment aging dan replacement planning.",
      metrics: [
        { icon: "Wrench", label: "Usia Rata-rata", value: "6.5", unit: "tahun" },
        { icon: "AlertCircle", label: "Downtime", value: "8%", unit: "per bulan" },
        { icon: "DollarSign", label: "Biaya Maintenance", value: "45", unit: "juta/tahun" }
      ],
      findings: [
        {
          type: "warning",
          text: "Pasteurizer utama berusia 8 tahun dengan maintenance cost meningkat 40% dalam 2 tahun terakhir"
        },
        {
          type: "warning",
          text: "Sistem refrigerasi aging dengan efisiensi energi menurun 25% dibanding standar baru"
        },
        {
          type: "positive",
          text: "Packaging equipment dalam kondisi baik dengan downtime minimal (2%)"
        },
        {
          type: "negative",
          text: "Tidak ada preventive maintenance schedule sistematis, mayoritas maintenance bersifat reaktif"
        }
      ],
      recommendations: [
        "Prioritas investasi: Pasteurizer baru (Rp 850 juta) dan cold storage expansion (Rp 450 juta)",
        "Implementasi preventive maintenance program dengan CMMS (Computerized Maintenance Management System)",
        "Upgrade sistem refrigerasi untuk efisiensi energi dan pengurangan biaya operasional 20%",
        "Establish equipment replacement fund dengan alokasi Rp 200 juta per tahun"
      ]
    },
    {
      id: "financial",
      type: "financial",
      title: "Analisis Keuangan",
      overview: "Analisis cashflow, margin operasional, dan sensitivitas keuangan terhadap fluktuasi harga input dan volume penjualan. Evaluasi kemampuan finansial untuk mendukung scale-up dan absorb shock.",
      metrics: [
        { icon: "TrendingUp", label: "Margin Operasional", value: "18%", unit: "rata-rata" },
        { icon: "DollarSign", label: "Revenue Bulanan", value: "850", unit: "juta IDR" },
        { icon: "Activity", label: "Cashflow Buffer", value: "2.5", unit: "bulan" }
      ],
      findings: [
        {
          type: "positive",
          text: "Margin operasional 18% stabil dengan tren positif 2% year-over-year"
        },
        {
          type: "warning",
          text: "Cashflow sensitif terhadap fluktuasi harga susu dengan break-even pada kenaikan 15%"
        },
        {
          type: "warning",
          text: "Working capital terbatas dengan cashflow buffer hanya 2.5 bulan operasional"
        },
        {
          type: "positive",
          text: "Debt-to-equity ratio sehat di 0.6 dengan akses ke credit line Rp 500 juta"
        }
      ],
      recommendations: [
        "Tingkatkan cashflow buffer menjadi minimal 4 bulan operasional melalui profit retention",
        "Implementasi hedging strategy untuk mitigasi risiko fluktuasi harga susu",
        "Diversifikasi revenue stream dengan produk margin tinggi (yogurt, keju) untuk stabilitas finansial",
        "Secure additional credit facility Rp 1 miliar untuk mendukung scale-up investment"
      ]
    },
    {
      id: "market",
      type: "market",
      title: "Analisis Pasar",
      overview: "Evaluasi struktur customer base, ketergantungan pembeli, dan positioning pasar. Analisis mencakup customer concentration risk dan peluang diversifikasi channel.",
      metrics: [
        { icon: "Users", label: "Total Customers", value: "28", unit: "aktif" },
        { icon: "Building2", label: "Institusional", value: "65%", unit: "revenue" },
        { icon: "TrendingUp", label: "Growth Rate", value: "12%", unit: "YoY" }
      ],
      findings: [
        {
          type: "negative",
          text: "Ketergantungan tinggi pada pembeli institusional (65% revenue) dengan 3 customer utama menyumbang 48%"
        },
        {
          type: "warning",
          text: "Diversifikasi produk terbatas dengan 80% revenue dari susu pasteurisasi plain"
        },
        {
          type: "positive",
          text: "Pertumbuhan retail channel 25% YoY menunjukkan potensi diversifikasi"
        },
        {
          type: "positive",
          text: "Brand recognition kuat di pasar lokal dengan customer retention rate 92%"
        }
      ],
      recommendations: [
        "Kurangi ketergantungan institusional menjadi maksimal 45% revenue dalam 12 bulan",
        "Ekspansi retail channel dengan target 40% revenue contribution melalui modern trade dan e-commerce",
        "Diversifikasi produk dengan launching yogurt dan flavored milk untuk margin lebih tinggi",
        "Develop B2B partnership dengan cafe dan restaurant untuk stable revenue stream"
      ]
    },
    {
      id: "governance",
      type: "governance",
      title: "Analisis Tata Kelola",
      overview: "Evaluasi struktur organisasi, sistem kontrol internal, dan kesiapan manajemen untuk scale-up. Termasuk assessment terhadap human capital dan succession planning.",
      metrics: [
        { icon: "Users", label: "Total Karyawan", value: "42", unit: "orang" },
        { icon: "Award", label: "Sertifikasi", value: "3", unit: "aktif" },
        { icon: "Shield", label: "Compliance Score", value: "88%", unit: "audit" }
      ],
      findings: [
        {
          type: "positive",
          text: "Struktur organisasi jelas dengan separation of duties dan sistem approval berlapis"
        },
        {
          type: "positive",
          text: "Sertifikasi HACCP, Halal, dan SNI aktif dengan audit compliance score 88%"
        },
        {
          type: "warning",
          text: "Succession planning lemah dengan ketergantungan tinggi pada founder untuk keputusan strategis"
        },
        {
          type: "warning",
          text: "Skill gap di area digital marketing dan data analytics untuk mendukung growth"
        }
      ],
      recommendations: [
        "Develop succession plan dengan grooming 2-3 key managers untuk leadership roles",
        "Hire specialized talent untuk digital marketing dan business analytics",
        "Implementasi ERP system untuk integrated business management dan real-time reporting",
        "Establish board of advisors dengan expertise di dairy industry dan scale-up management"
      ]
    }
  ];

  const strategies = [
    {
      title: "Diversifikasi Pemasok Susu",
      priority: "critical",
      timeline: "0-3 bulan",
      cost: "Rp 150 juta",
      description: "Program komprehensif untuk mengurangi konsentrasi risiko pemasok dari 78% (3 pemasok) menjadi maksimal 50% dengan onboarding 15-20 pemasok baru berkualitas.",
      steps: [
        "Mapping dan identifikasi 30 peternak potensial dalam radius 50km dengan kapasitas produksi minimal 100 liter/hari",
        "Implementasi program quality training dan certification untuk pemasok baru dengan standar ISUKA",
        "Setup sistem monitoring kualitas real-time dengan milk analyzer di collection points",
        "Negosiasi kontrak jangka panjang dengan insentif volume dan bonus kualitas",
        "Establish backup supplier network dengan 10 peternak standby untuk emergency supply"
      ],
      expectedImpact: [
        "Pengurangan supply concentration risk dari 78% menjadi 50% dalam 3 bulan",
        "Peningkatan supply stability dengan buffer capacity 25%",
        "Penurunan rejection rate dari 12% menjadi 5% melalui quality program",
        "Mitigasi risiko supply shock dengan diversified supplier base"
      ],
      kpis: [
        "Jumlah pemasok aktif: Target 60+ dalam 3 bulan",
        "Konsentrasi top 3 suppliers: Maksimal 50% total volume",
        "Rejection rate: Di bawah 5%",
        "Supply stability index: Minimal 85%"
      ]
    },
    {
      title: "Upgrade Kapasitas Pasteurisasi",
      priority: "high",
      timeline: "3-6 bulan",
      cost: "Rp 850 juta",
      description: "Investasi strategis untuk menghilangkan bottleneck produksi utama dengan upgrade pasteurizer dari 3.000 liter menjadi 5.000 liter per batch, meningkatkan throughput 67%.",
      steps: [
        "Vendor selection dan procurement pasteurizer 5.000 liter dengan teknologi energy-efficient",
        "Site preparation dan utility upgrade (steam, cooling water, electrical)",
        "Installation dan commissioning dengan minimal production disruption",
        "Staff training untuk operasi dan maintenance equipment baru",
        "Validation dan certification sesuai standar HACCP dan SNI"
      ],
      expectedImpact: [
        "Peningkatan kapasitas produksi dari 3.000 liter menjadi 5.000 liter per batch (+67%)",
        "Eliminasi bottleneck utama dan peningkatan overall throughput 40%",
        "Pengurangan biaya energi per liter 15% dengan teknologi baru",
        "Readiness untuk scale-up hingga 150% kapasitas saat ini"
      ],
      kpis: [
        "Batch capacity: 5.000 liter",
        "Daily throughput: Minimal 15.000 liter",
        "Energy efficiency: Pengurangan 15% per liter",
        "Downtime: Maksimal 2% per bulan"
      ]
    },
    {
      title: "Ekspansi Cold Storage",
      priority: "high",
      timeline: "2-4 bulan",
      cost: "Rp 450 juta",
      description: "Penambahan kapasitas penyimpanan dingin dari 8.000 liter menjadi 15.000 liter untuk buffer supply 5 hari dan mitigasi fluktuasi pasokan musiman.",
      steps: [
        "Design dan engineering cold storage expansion dengan kapasitas 7.000 liter tambahan",
        "Procurement refrigeration system dengan energy-efficient compressor",
        "Construction dan installation dengan insulation optimal",
        "Integration dengan existing cold chain system dan monitoring",
        "Testing dan validation temperature stability dan energy consumption"
      ],
      expectedImpact: [
        "Peningkatan buffer capacity dari 2 hari menjadi 5 hari operasional",
        "Mitigasi risiko supply disruption dengan adequate safety stock",
        "Fleksibilitas untuk menerima supply bulk dengan harga lebih baik",
        "Pengurangan waste dari supply fluctuation 30%"
      ],
      kpis: [
        "Total cold storage: 15.000 liter",
        "Buffer days: Minimal 5 hari",
        "Temperature stability: ±0.5°C",
        "Energy efficiency: Maksimal 2.5 kWh per 1000 liter-day"
      ]
    },
    {
      title: "Diversifikasi Channel Penjualan",
      priority: "medium",
      timeline: "3-9 bulan",
      cost: "Rp 200 juta",
      description: "Program strategis untuk mengurangi ketergantungan pada pembeli institusional dari 65% menjadi 45% dengan ekspansi retail, e-commerce, dan B2B partnership.",
      steps: [
        "Develop retail distribution network dengan target 50 modern trade outlets",
        "Launch e-commerce channel melalui marketplace dan direct-to-consumer platform",
        "Establish B2B partnership dengan 20 cafe dan restaurant untuk stable offtake",
        "Implement digital marketing campaign untuk brand awareness dan customer acquisition",
        "Setup logistics dan fulfillment infrastructure untuk multi-channel distribution"
      ],
      expectedImpact: [
        "Pengurangan institutional dependency dari 65% menjadi 45% revenue",
        "Peningkatan retail contribution dari 20% menjadi 40% revenue",
        "Diversifikasi customer base dengan 100+ active retail customers",
        "Margin improvement 3-5% dari higher-margin retail channel"
      ],
      kpis: [
        "Institutional revenue: Maksimal 45% total",
        "Retail revenue: Minimal 40% total",
        "Active retail customers: 100+",
        "E-commerce contribution: Minimal 10% revenue"
      ]
    }
  ];

  const handleExportPDF = () => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
      alert('Laporan PDF berhasil diunduh!\n\nFile: DD-ISUKA-2026-001.pdf\nUkuran: 2.4 MB\nLokasi: Downloads folder');
    }, 2000);
  };

  const handleShareReport = () => {
    alert('Fitur berbagi laporan akan segera tersedia.\n\nAnda dapat mengirim laporan melalui email ke stakeholder yang berwenang dengan enkripsi end-to-end.');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20">
        <div className="container-executive py-8 md:py-12 space-y-6">
          <ReportHeader 
            companyData={companyData}
            reportDate={reportDate}
            reportId={reportId}
          />

          <ExecutiveSummary summaryData={summaryData} />

          <RiskHeatmap riskData={riskData} />

          <DetailedAnalysis analysisData={analysisData} />

          <MitigationStrategies strategies={strategies} />

          <ReportActions 
            onExportPDF={handleExportPDF}
            onShareReport={handleShareReport}
          />

          {isExporting && (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
              <div className="bg-card rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 animate-pulse">
                    <Icon name="Download" size={32} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">Mengunduh Laporan</h3>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Sedang memproses dan mengunduh laporan PDF...
                  </p>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary animate-pulse" style={{ width: '70%' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ExecutiveReport;